import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import SelectTargetMenuModal from 'components/SelectTargetMenuModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { TargetMenu } from 'models/common';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError, TARGET_MENU_DISPLAY_CONTROL_TIMING } from 'utils';
import displayTimingGroupService from '../../displayTimingGroupService';
import { DisplayTimingGroupOption } from '../../models';
// @ts-ignore react-scripts
import './style.scss';

export interface TargetMenuDisplayTimingGroupModalProps {
  displayTimingGroupId: number;
  groupName?: string;
  groups?: DisplayTimingGroupOption[];
  onClose?: (isDataChanged: boolean) => void;
}

function TargetMenuDisplayTimingGroupModal({
  displayTimingGroupId,
  groupName = '',
  groups = [],
  onClose,
}: TargetMenuDisplayTimingGroupModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // refs
  // tracks whether any add/remove/move succeeded, so the list behind the modal only reloads when needed
  const isDataChangedRef = useRef(false);

  // states
  const [menus, setMenus] = useState<TargetMenu[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [moveTargetGroupId, setMoveTargetGroupId] = useState('');
  const [showSelectTargetMenuModal, setShowSelectTargetMenuModal] = useState(false);

  const selectedCount = selectedIds.length;
  const hasSelection = selectedCount > 0;
  // only 有効 groups can be moved into; the menu-side flow rejects disabled groups too
  const moveTargetGroups = groups.filter((group) => group.id !== displayTimingGroupId && group.is_active);

  // side effects
  useEffect(() => {
    loadMenus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTimingGroupId]);

  // functions
  async function loadMenus() {
    try {
      const response = await displayTimingGroupService.getTargetMenuList({
        display_timing_group_id: displayTimingGroupId,
      });
      setMenus(response.data);
      setSelectedIds([]);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function toggleOne(id: number, checked: boolean) {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id)));
  }

  function showSuccess(message: string) {
    showGlobalModal(() => <AlertModal title={t('App.info')} message={message} />);
  }

  // events
  function handleClose(isDataChanged: boolean = isDataChangedRef.current) {
    onClose?.(isDataChanged);
  }

  async function removeSelected() {
    try {
      const response = await displayTimingGroupService.deleteTargetMenu({
        display_timing_group_id: displayTimingGroupId,
        menu_ids: selectedIds,
      });
      isDataChangedRef.current = true;
      setMenus(response.data);
      setSelectedIds([]);
      showSuccess(response.message);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function handleRemoveSelected() {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('DisplayTimingGroup.target_menu_modal.confirm_remove')}
        onConfirm={removeSelected}
      />
    ));
  }

  async function handleReflectSelectTargetMenuModal(menuIds: number[]) {
    setShowSelectTargetMenuModal(false);
    if (menuIds.length === 0) {
      return;
    }

    try {
      const response = await displayTimingGroupService.addTargetMenu({
        display_timing_group_id: displayTimingGroupId,
        menu_ids: menuIds,
      });
      isDataChangedRef.current = true;
      setMenus(response.data);
      setSelectedIds([]);
      showSuccess(response.message);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  async function handleMoveSelected() {
    const targetGroupId = Number(moveTargetGroupId);
    if (!hasSelection || !targetGroupId) {
      return;
    }

    try {
      // Single FK: assigning to another group automatically detaches from the current one.
      const response = await displayTimingGroupService.addTargetMenu({
        display_timing_group_id: targetGroupId,
        menu_ids: selectedIds,
      });
      isDataChangedRef.current = true;
      setMoveTargetGroupId('');
      await loadMenus();
      showSuccess(response.message);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  return (
    <>
      <Modal
        size="lg"
        dialogClassName="display-timing-group-target-menu"
        show
        backdrop="static"
        centered
        onHide={() => handleClose()}
      >
        <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <FeatureTitleBar title={t('DisplayTimingGroup.target_menu_modal.head_title')}></FeatureTitleBar>
        </Modal.Header>

        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <div className="display-timing-group-target-menu__info">
            <div className="display-timing-group-target-menu__info-group">
              {t('DisplayTimingGroup.target_menu_modal.group_label', { name: groupName })}
            </div>
            <div className="display-timing-group-target-menu__info-count">
              {t('DisplayTimingGroup.target_menu_modal.menu_count', { count: menus.length })}
            </div>
          </div>

          <div className="display-timing-group-target-menu__actions">
            <div className="display-timing-group-target-menu__actions-add">
              <div className="display-timing-group-target-menu__actions-label">
                {t('DisplayTimingGroup.target_menu_modal.add_label')}
              </div>
              <Button className="btn--dark-green" onClick={() => setShowSelectTargetMenuModal(true)}>
                {t('DisplayTimingGroup.target_menu_modal.add_to_group')}
              </Button>
            </div>

            <div className="display-timing-group-target-menu__actions-divider" />

            <div className="display-timing-group-target-menu__actions-selected">
              <div className="display-timing-group-target-menu__actions-label">
                {t('DisplayTimingGroup.target_menu_modal.selected_label', { count: selectedCount })}
              </div>
              <div className="display-timing-group-target-menu__actions-buttons">
                <Button className="btn--red" disabled={!hasSelection} onClick={handleRemoveSelected}>
                  {t('DisplayTimingGroup.target_menu_modal.remove_from_group')}
                </Button>
                <div className="display-timing-group-target-menu__actions-move">
                  <Form.Select
                    className="display-timing-group-target-menu__select"
                    value={moveTargetGroupId}
                    onChange={(e) => setMoveTargetGroupId(e.target.value)}
                    disabled={!hasSelection}
                  >
                    <option value="">{t('DisplayTimingGroup.target_menu_modal.no_selection')}</option>
                    {moveTargetGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.group_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    className="display-timing-group-target-menu__btn-move"
                    disabled={!hasSelection || !moveTargetGroupId}
                    onClick={handleMoveSelected}
                  >
                    {t('DisplayTimingGroup.target_menu_modal.move_to_group')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="display-timing-group-target-menu__table-wrapper">
            <Table className="display-timing-group-target-menu__table" hover>
              <thead>
                <tr>
                  <th className="col-select">{t('DisplayTimingGroup.target_menu_modal.selection')}</th>
                  <th className="col-code">{t('DisplayTimingGroup.target_menu_modal.menu_code')}</th>
                  <th className="col-name">{t('DisplayTimingGroup.target_menu_modal.menu_name')}</th>
                </tr>
              </thead>
              <tbody>
                {menus.map((menu) => (
                  <tr key={menu.id}>
                    <td className="col-select text-center">
                      <Form.Check
                        type="checkbox"
                        className="custom-checkbox"
                        checked={selectedIds.includes(menu.id)}
                        onChange={(e) => toggleOne(menu.id, e.target.checked)}
                      />
                    </td>
                    <td className="col-code text-center">{menu.cd}</td>
                    <td className="col-name text-center">{menu.name}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {showSelectTargetMenuModal &&
        createPortal(
          <SelectTargetMenuModal
            targetSourceType={TARGET_MENU_DISPLAY_CONTROL_TIMING}
            targetName={groupName}
            exceptMenuIds={menus.map((menu) => menu.id)}
            onReflect={handleReflectSelectTargetMenuModal}
            onClose={() => setShowSelectTargetMenuModal(false)}
          />,
          document.body
        )}
    </>
  );
}

export default TargetMenuDisplayTimingGroupModal;
